
import { defineComponent } from 'vue'
import { useStore, mapGetters } from 'vuex'
import { isAndroidNative as isAndroid, isIOSNative as isIOS } from '@xunlei/universal-native-api'
import remove from 'lodash/remove'

import { getYunPanFileList } from '@remote/api/adapter/xos-mqtt'

import global from '@remote/store/constantStore'
import { usePathSelector } from '@remote/store/hezi-file/path-selector'
import { useTasks } from '@remote/store/task'

import { parseTime, isToday, bytesToSize } from '@remote/utils'
import { isLargeViewPort, getErrorMessage } from '@remote/utils/base'
import { toast } from '@remote/utils/dialog'
import { isHeziNative } from '@remote/utils/platform'
import { uuidv4, logInfo, logError } from '@remote/utils/logger'
import { closeActivity, registerEventHandle, removeEventHandle } from '@remote/utils/jsbridge/hezi'

import LoadMore from '@remote/components/LoadMore.vue'
import HeziPathSelector from './downloader.vue'
import { isSupportPreview, previewFile, addDownloadTaskByNative } from './helper'
import type { IFileInfo } from './type'

function logs (..._args) {
  console.log('[PanList]', ..._args)
}

export default defineComponent({
  name: 'PanList',
  components: {
    HeziPathSelector,
    LoadMore,
  },
  props: {
    totalList: {
      type: Array,
      default: () => [],
    },
    parentId: {
      type: String,
      default: '',
    },
    filters: {
      type: String,
      default: JSON.stringify({
        trashed: {
          eq: false,
        },
        phase: {
          eq: 'PHASE_TYPE_COMPLETE',
        },
        /* kind: {
          eq: 'drive#folder'
        } */
      }),
    },
    limit: {
      type: Number,
      default: 20,
    },
    hasFooter: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['closePath', 'cancel'],
  setup: () => {
    document.title = '从云盘创建下载任务'
    const { selectedPath } = usePathSelector()
    const taskStore = useTasks()
    return {
      store: useStore(),
      selectedPath,
      taskStore,
    }
  },
  data () {
    return {
      traceId: uuidv4(),
      all: {} as { [fileId: string]: IFileInfo }, // 缓存文件
      caches: {}, // 根据 parentId来缓存对应的文件id
      files: ([] as string[]),
      next_page_token: '',
      currentParentId: '',
      folderPath: [
        {
          name: '我的云盘',
          id: '',
          _id: 0,
          showName: '我的云盘',
        },
      ],
      isLoading: false,
      chooseFile: [] as IFileInfo[],
      isSelecting: false,
      isSelectAll: false,
      canDownloadedFileNum: 0,
      isUnMounted: false,
      hasMore: true,
      isLargeViewPort: isLargeViewPort(),
      isAndroid,
      isIOS,
      isHeziNative,
      requestId: 0,
    }
  },
  computed: {
    ...mapGetters(['isFullScreen']),
    target (): string | undefined {
      return this.store.state.curDeviceRunner?.target
    },
    hzSelectorVisible (): boolean {
      const hasSelected = !!this.chooseFile.length
      return hasSelected || this.isLargeViewPort
    },
  },
  watch: {
    target (val, old) {
      if (val && val !== old) {
        this.getFiles(this.parentId, 'connected')
      }
    },
  },
  mounted () {
    if (this.target) {
      this.getFiles(this.parentId, 'mounted')
    }
    window.addEventListener('resize', this.onresize)
    registerEventHandle('event_on_back_pressed', this.onBack)
  },
  unmounted () {
    this.isUnMounted = true
    window.removeEventListener('resize', this.onresize)
    removeEventHandle('event_on_back_pressed')
  },
  methods: {
    isToday,
    parseTime,
    bytesToSize,
    onresize () {
      this.isLargeViewPort = isLargeViewPort()
    },
    onBack () {
      if (this.isLargeViewPort) {
        this.$router.go(-1)
      } else {
        if (this.folderPath.length <= 1) {
          if (isHeziNative) {
            closeActivity()
          } else {
            this.$router.go(-1)
          }
        } else {
          this.handleClickBreadCrumb(this.folderPath.at(-2))
        }
      }
      // hack 安卓盒子第二次响应手势监听异常
      registerEventHandle('event_on_back_pressed', this.onBack)
      return true
    },
    hide () {
      this.currentParentId = ''
      this.folderPath = [
        {
          name: '我的云盘',
          id: '',
          _id: 0,
          showName: '我的云盘',
        },
      ]
      this.folderPathLite = []
      this.getFiles('', 'hide')
      this.$emit('closePath', false)
    },
    async getFiles (id = '', caller = '') {
      const provider = this.$route.params?.provider
      logs('[getFiles]', id, caller, provider)
      if (this.isUnMounted) return
      this.files = this.caches[id || '0'] || []
      const params = {
        provider,
        parent_id: id,
        limit: this.limit,
        with_audit: true,
        filters: this.filters,
        page_token: this.next_page_token,
      }

      this.isLoading = true
      this.requestId+=1
      try {
        const result = await getYunPanFileList(params, this.requestId)

        const { next_page_token, files, requestId } = result
        if (this.isUnMounted || params.parent_id !== this.currentParentId || this.requestId !== requestId) return
        this.next_page_token = next_page_token

        const dump = remove(files, { name: '我的转存' })
        const resource = remove(files, { name: '我的资源' })
        const compress = remove(files, { name: '在线解压' })
        const zhan = remove(files, { name: '小站收藏' })
        const hezi = remove(files, { name: '迅雷盒子' })

        const defaultDirs = dump.concat(resource, compress, zhan).map(item => ({ ...item, isDefault: true } as any))
        const restAllFloders = files.reverse().filter(item => item.kind === 'drive#folder')
        const restAllFliles = files.reverse().filter(item => item.kind === 'drive#file')
        let auditDisableNum = 0

        const fileList = defaultDirs.concat(hezi, restAllFloders, restAllFliles).map(v => {
          const status = v.audit?.status
          const isAuditDisable = (status === 'STATUS_SENSITIVE_RESOURCE' || status === 'STATUS_SENSITIVE_WORD') && v.kind === 'drive#file'
          v.auditDisableType = isAuditDisable ? 1 : 0
          v.checkType = 0
          this.all[v.id] = v
          if (isAuditDisable) auditDisableNum += 1
          return v.id
        })

        const nextFiles = params.page_token ? ([] as string[]).concat(this.files, fileList) : fileList
        this.caches[id || '0'] = nextFiles
        this.files = nextFiles
        logs('当前文件列表:', this.files, files, params.page_token, requestId)
        this.canDownloadedFileNum = nextFiles.length - defaultDirs.length - auditDisableNum
      } catch (err) {
        console.error(err)
        const msg = getErrorMessage(err)
        toast({ type: 'error', message: msg })
        logError('yunpan', {
          traceId: this.traceId,
          category: 'yunpan_files',
          action: 'get_yunpan_files',
          desc: '获取云盘文件列表时出现异常',
          params,
          error: err,
          errorText: msg,
        })
      } finally {
        this.isLoading = false
      }
    },
    loadMore () {
      if (this.isLoading) return
      this.getFiles(this.currentParentId, 'loadMore')
    },
    handleCancel () {
      this.chooseFile.forEach(file => {
        this.all[file.id].checkType = 0
      })
      this.chooseFile = []
      this.isSelectAll = false
      this.isSelecting = false
    },
    // 点击面包屑更新文件列表
    handleClickBreadCrumb (folder) {
      if (this.chooseFile.length > 0 || this.isSelecting && !this.isLargeViewPort || !folder) return
      logs('folder :>> ', folder, this.all, this.folderPath, this.folderPath.length)
      logs('object :>> ', folder._id === this.folderPath.length - 1)
      if (folder.id === '' && folder._id === 0) {
        this.hide()
        return
      }
      const file = this.all[folder.id]
      if (file._id !== this.folderPath.length - 1) {
        this.currentParentId = file.id
      }
      this.next_page_token = ''
      this.handleFolderPath(file)
      this.getFiles(this.currentParentId, 'clickBreadCrumbHandler')
    },
    handleClickItem (id: string) {
      const file: IFileInfo = this.all[id]

      const isSelectedState = this.isLargeViewPort ? !!this.chooseFile.length : this.isSelecting
      if (isSelectedState) {
        if (file.auditDisableType === 1) return toast({ type: 'error', message: '违规资源，无法下载' })
        // 特殊文件夹禁止选择
        if (file.isDefault) return
        return this.handleSelectItem(file)
      } else {
        if (file.auditDisableType === 1) return toast({ type: 'error', message: '违规资源，无法打开' })

        if (file.kind == 'drive#file') {
          // 文件消费
          logs('文件消费:', file)
          if (isSupportPreview(file.name)) {
            const provider = this.$route.params?.provider
            previewFile(file, provider)
          } else {
            toast({ type: 'info', message: '请下载到设备后再打开' })
          }
        } else {
          // 切换目录
          if (file._id !== this.folderPath.length - 1) {
            this.currentParentId = file.id
          }
          this.next_page_token = ''
          this.handleFolderPath(file)
          this.getFiles(this.currentParentId, 'handleClickItem')
        }
      }
    },
    handleOnPressItem (id) {
      if (this.isLargeViewPort) return
      this.handleSelectItem(this.all[id])
    },
    handleSelectItem (file: IFileInfo) {
      this.isSelecting = true
      if (file.checkType === 1) {
        const index = this.chooseFile.findIndex((item) => item.id === file.id)
        this.chooseFile.splice(index, 1)
        file.checkType = 0
      } else {
        this.chooseFile.push(file)
        file.checkType = 1
      }
      this.isSelectAll = this.chooseFile.length === this.canDownloadedFileNum

      // 取消选中最后一项数据退出编辑态
      const isSelectNone = this.chooseFile.length === 0
      if (isSelectNone) {
        this.isSelecting = false
      }
    },
    handleSelectAll () {
      this.isSelectAll = !this.isSelectAll
      this.files.forEach(id => {
        const file = this.all[id]
        if (file.auditDisableType !== 0) return
        if (this.isSelectAll) {
          if (!file.isDefault && file.checkType !== 1) {
            this.chooseFile.push(file)
            file.checkType = 1
          }
        } else {
          if (!file.isDefault && file.checkType === 1) {
            file.checkType = 0
          }
        }
      })
      if (!this.isSelectAll) this.chooseFile = []

      // 取消全选退出编辑态
      const isSelectNone = this.chooseFile.length === 0
      if (isSelectNone) {
        this.isSelecting = false
      }
      this.isSelecting = this.isSelectAll
    },
    handleFolderPath (file) {
      let ret = this.folderPath.filter(elm => {
        return file.id === elm.id
      })
      if (ret.length) {
        this.folderPath.splice(ret[0]._id + 1, this.folderPath.length - 1)
      } else {
        const substrEnd = 9
        this.folderPath.push({
          id: file.id,
          name: file.name,
          showName: file.name.length > 5 ? `${file.name.substr(0, substrEnd)}...` : file.name,
          _id: this.folderPath.length,
        })
      }
    },
    async confirm () {
      const traceId = this.traceId
      const urls: string[] = []
      const downloadPath = this.selectedPath.value.path
      const links = this.chooseFile.map(file => {
        const userId = global.userId
        const fileType = file.kind === 'drive#folder' ? 'folder' : 'file'
        const url = `xdrive://hezi/${userId}/${fileType}/${file.id}`
        return { url, name: file.name, icon: file.icon_link, fileType }
      })
      links.forEach(link => {
        const { url, name, icon } = link
        urls.push(url)
        this.taskStore.setMap(url, -4, url, name, icon)
      })
      try {
        if (!urls.length) {
          throw new Error('无法下载选中内容')
        }
        logs('下载选中文件:', urls, downloadPath)
        logInfo('transport', {
          traceId,
          category: 'yunpan',
          action: 'submit_download_task',
          desc: '立即下载选中文件',
          params: { urls, downloadPath },
        })
        // 下载使用客户端能力下载
        if (isHeziNative) {
          await addDownloadTaskByNative(links, downloadPath)
        } else {
          await this.store.dispatch('getResource', {
            urls: urls.join('\n'),
            downloadPath,
          })
        }

        if (isHeziNative) {
          closeActivity()
        } else {
          this.$router.go(-1)
        }
      } catch (err) {
        console.error(err)
        const msg = getErrorMessage(err)
        toast({ type: 'error', message: msg })
        logError('transport', {
          traceId,
          category: 'yunpan',
          action: 'submit_download_task',
          desc: '下载选中文件出现错误',
          params: { urls, downloadPath },
          error: err,
          errorText: msg,
        })
      }
    },
    async download () {
      for (let i = 0; i < this.chooseFile.length; i++) {
        const file = this.chooseFile[i]
        if (file.chooseType !== 0) {
          try {
            await this.store.dispatch('download', { file })
          } catch (err) {
            console.log(err)
          }
        }

      }
      this.store.commit('update', { taskType: 'ing' })
      setTimeout(() => this.store.dispatch('getTasks', { isReplay: true }), 2000)
      this.$router.go(-1)
    },
  },
})
